import {useNavigation} from '@react-navigation/native';
import React from 'react';
import {useSelector} from 'react-redux';

import {Alert} from '../../components/Alert';
import {Constants} from '../../constants';
import {Alerts, Strings, interpolate} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import type {
  RootStackParamList,
  RootStackScreenProps,
} from '../../navigators/RootStackNavigator';
import {toHeatProfileSelect, toMoodLightLibrary} from '../../navigators/util';
import {analytics} from '../../services/analytics';
import {getAppUrl} from '../../util/url';
import {shareApi} from '../api/apis';
import {allHeatProfilesMapSelector} from '../redux/slices/profilesSlice';
import {userSelector} from '../redux/slices/userSlice';
import {HeatProfile} from '../types';
import {shareUrl} from '../utils/shareUrl';
import {useAsyncFn} from './useAsyncFn';
import {useUserVerifiedGuard} from './useUserVerifiedGuard';

type Navigation = RootStackScreenProps<keyof RootStackParamList>['navigation'];

interface ShareHeatProfileByIdOptions {
  shareId: string;
  heatProfileId: string;
  heatProfileName: string;
}

export const useShareHeatProfile = () => {
  const user = useSelector(userSelector);
  const profilesMap = useSelector(allHeatProfilesMapSelector);

  const {navigate} = useNavigation<Navigation>();
  const {isUserVerified} = useUserVerifiedGuard();

  const [shareableHeatProfile, createShareHeatProfile] = useAsyncFn(
    async (heatProfile: HeatProfile) => {
      return shareApi
        .createShareProfileId({heatProfileCreateShareDto: {id: heatProfile.id}})
        .then(r => ({
          ...r,
          data: {
            ...r.data,
            heatProfileId: heatProfile.id,
            heatProfileName: heatProfile.name,
          },
        }));
    },
    [],
  );

  const shareHeatProfileById = React.useCallback(
    async ({
      shareId,
      heatProfileId,
      heatProfileName,
    }: ShareHeatProfileByIdOptions) => {
      const url = `${getAppUrl()}/${Constants.SHARE_PATH}/${shareId}`;

      await shareUrl({url})
        .catch(() => void 0)
        .then(() => {
          analytics.trackEvent('share', {
            content_type: 'heat_profile',
            item_id: heatProfileId,
            item_name: heatProfileName,
          });
        });
    },
    [],
  );

  React.useEffect(() => {
    if (shareableHeatProfile.error) {
      Alert.alert(Alerts.PROFILE_SHARE_ERROR);

      return;
    }

    if (!shareableHeatProfile.value) return;

    const {shareId, heatProfileId, heatProfileName} =
      shareableHeatProfile.value.data;

    Alert.alert(
      interpolate(Alerts.SHARE_PROFILE_READY_TITLE, {
        profileName: heatProfileName,
      }),
      interpolate(Alerts.SHARE_PROFILE_READY_BODY, {
        profileName: heatProfileName,
      }),
      [
        {
          style: 'cancel',
          text: 'Cancel',
        },
        {
          onPress: () => {
            shareHeatProfileById({
              shareId,
              heatProfileId,
              heatProfileName,
            });
          },
          text: 'Share',
        },
      ],
    );
  }, [
    shareableHeatProfile.error,
    shareableHeatProfile.value,
    shareHeatProfileById,
  ]);

  const onLoginClick = React.useCallback(() => {
    navigate(Screens.Login, {
      redirect: toHeatProfileSelect.encode(),
    });

    navigate(Screens.Login, {
      redirect: toMoodLightLibrary().encode(),
    });
  }, [navigate]);

  const share = React.useCallback(
    async (id: string) => {
      if (!user) {
        onRequireLogin(
          Alerts.PROFILE_SHARE_TITLE,
          Alerts.PROFILE_LOGIN_SHARE_MESSAGE,
          onLoginClick,
        );

        return;
      }

      if (!isUserVerified()) return;

      const heatProfile = profilesMap[id];

      if (!heatProfile) return;

      await createShareHeatProfile(heatProfile);
    },
    [user, profilesMap, onLoginClick, isUserVerified, createShareHeatProfile],
  );

  return React.useMemo(
    () => [shareableHeatProfile, share] as const,
    [shareableHeatProfile, share],
  );
};

const onRequireLogin = (title: string, message: string, onLogin: () => void) =>
  Alert.alert(
    title,
    message,
    [
      {
        text: Strings.LOG_IN,
        onPress: () => onLogin(),
        style: 'default',
      },
      {text: Strings.CANCEL, style: 'cancel'},
    ],
    {cancelable: false},
  );
